import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useStateContext } from '../../../../../contexts/ContextProvider';

export const useLivrari = () => {
  const { token } = useStateContext();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shipmentStatuses, setShipmentStatuses] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [overallSummary, setOverallSummary] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const [filters, setFilters] = useState({
    search: '',
    purchase_request_id: null,
    offer_id: null,
    courier_id: [],
    status_id: [],
    awb: '',
    sort_by: 'date_added',
    sort_order: 'desc'
  });

  // Funcție pentru aplicarea filtrelor
  const applyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current: 1 })); // Resetăm la prima pagină
  }, []);

  // Funcție pentru resetarea filtrelor
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      purchase_request_id: null,
      offer_id: null,
      courier_id: [],
      status_id: [],
      awb: '',
      sort_by: 'date_added',
      sort_order: 'desc'
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  }, []);

  // Funcție pentru schimbarea paginii
  const handleTableChange = useCallback((newPagination) => {
    setPagination(newPagination);
  }, []);

  // Funcția principală pentru fetch-ul shipment-urilor
  const fetchShipments = useCallback(async () => {
    if (!token) return;

    setLoading(true);

    try {
      const params = new URLSearchParams({
        token,
        page: pagination.current.toString(),
        page_size: pagination.pageSize.toString()
      });

      // Adăugăm filtrele
      if (filters.search) params.append('search', filters.search);
      if (filters.purchase_request_id) params.append('purchase_request_id', filters.purchase_request_id);
      if (filters.offer_id) params.append('offer_id', filters.offer_id);
      
      // Pentru filtrele multiple, le trimitem ca string comma-separated
      if (filters.courier_id && filters.courier_id.length > 0) {
        params.append('courier_id', filters.courier_id.join(','));
      }
      if (filters.status_id && filters.status_id.length > 0) {
        params.append('status_id', filters.status_id.join(','));
      }
      
      if (filters.awb) params.append('awb', filters.awb);

      console.log('🔄 Fetching shipments...', params.toString());

      const response = await fetch(`${process.env.REACT_APP_API_URL}/shipments?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Shipments data received:', data);
        
        // Debug datele shipment-urilor individual
        if (data.shipments && Array.isArray(data.shipments)) {
          console.log('🔍 Debugging individual shipments:');
          data.shipments.forEach((shipment, index) => {
            console.log(`Shipment ${index + 1} (ID: ${shipment.id}):`, {
              id: shipment.id,
              produse: shipment.produse,
              produseLength: shipment.produse?.length,
              produseType: typeof shipment.produse,
              isArray: Array.isArray(shipment.produse)
            });
          });
        }
        
        setShipments(data.shipments || []);
        
        // Actualizăm paginația
        setPagination(prev => ({
          ...prev,
          current: data.page || 1,
          total: data.total || 0,
          pageSize: data.page_size || prev.pageSize
        }));

        // Calculez un sumar general
        if (data.shipments && data.shipments.length > 0) {
          const summary = calculateOverallSummary(data.shipments);
          setOverallSummary(summary);
        } else {
          setOverallSummary({
            total_shipments: 0,
            total_products: 0,
            total_weight: 0,
            unique_couriers: 0,
            status_counts: {}
          });
        }

      } else {
        console.error('❌ HTTP error:', response.status, response.statusText);
        message.error('Eroare la încărcarea livrărilor');
      }
    } catch (error) {
      console.error('❌ Network error:', error);
      message.error('Eroare de conexiune');
    } finally {
      setLoading(false);
    }
  }, [token, pagination.current, pagination.pageSize, filters]);

  // Funcție pentru calcularea sumarului general
  const calculateOverallSummary = useCallback((shipmentsData) => {
    if (!shipmentsData || shipmentsData.length === 0) {
      return {
        total_shipments: 0,
        total_products: 0,
        total_weight: 0,
        unique_couriers: 0,
        status_counts: {}
      };
    }

    // Helper function pentru calcularea produselor unui shipment
    const getShipmentProductsCount = (shipment) => {
      try {
        if (!shipment || !shipment.produse || !Array.isArray(shipment.produse)) {
          return 0;
        }
        return shipment.produse.length;
      } catch (error) {
        console.error('Error calculating products for shipment:', shipment?.id, error);
        return 0;
      }
    };

    const totalShipments = shipmentsData.length;
    const statusCounts = shipmentsData.reduce((acc, shipment) => {
      if (shipment.status_id) {
        acc[shipment.status_id] = (acc[shipment.status_id] || 0) + 1;
      }
      return acc;
    }, {});

    const totalProducts = shipmentsData.reduce((acc, shipment) => 
      acc + getShipmentProductsCount(shipment), 0
    );

    const totalWeight = shipmentsData.reduce((acc, shipment) => 
      acc + (parseFloat(shipment.weight_kgs) || 0), 0
    );

    const uniqueCouriers = new Set(
      shipmentsData.filter(s => s.courier_id).map(s => s.courier_id)
    ).size;

    console.log('📊 Overall summary calculated:', {
      totalShipments,
      totalProducts,
      totalWeight,
      uniqueCouriers,
      statusCounts
    });

    return {
      total_shipments: totalShipments,
      total_products: totalProducts,
      total_weight: totalWeight,
      unique_couriers: uniqueCouriers,
      status_counts: statusCounts
    };
  }, []);

  // Funcție pentru obținerea statusurilor shipment-urilor (defensivă)
  const fetchShipmentStatuses = useCallback(async () => {
    if (!token) return;

    try {
      // Încerc mai multe endpoint-uri posibile
      const possibleEndpoints = [
        '/shipment-status',
        '/shipment-statuses', 
        '/shipments/statuses'
      ];

      let success = false;
      
      for (const endpoint of possibleEndpoints) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}?token=${token}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setShipmentStatuses(Array.isArray(data) ? data : data.statuses || []);
            success = true;
            console.log('✅ Shipment statuses loaded from:', endpoint);
            break;
          }
        } catch (err) {
          continue; // Încerc următorul endpoint
        }
      }

      if (!success) {
        console.log('ℹ️ Nu s-au putut încărca statusurile de livrare - folosesc statusurile din răspunsurile shipment-urilor');
        // Extrag statusurile din datele existente ale shipment-urilor
        extractStatusesFromShipments();
      }
    } catch (error) {
      console.log('ℹ️ Endpoint pentru statusuri nu este disponibil - extrag din datele existente');
      extractStatusesFromShipments();
    }
  }, [token]);

  // Funcție pentru extragerea statusurilor din datele existente
  const extractStatusesFromShipments = useCallback(() => {
    if (shipments.length > 0) {
      const uniqueStatuses = [];
      const statusMap = new Map();
      
      shipments.forEach(shipment => {
        if (shipment.status_id && shipment.status_label && !statusMap.has(shipment.status_id)) {
          statusMap.set(shipment.status_id, {
            id: shipment.status_id,
            label: shipment.status_label,
            color: shipment.status_color || '#8c8c8c'
          });
        }
      });
      
      setShipmentStatuses(Array.from(statusMap.values()));
    }
  }, [shipments]);

  // Funcție pentru obținerea curierilor (defensivă)
  const fetchCouriers = useCallback(async () => {
    if (!token) return;

    try {
      // Încerc mai multe endpoint-uri posibile
      const possibleEndpoints = [
        '/curiers',
        '/couriers',
        '/shipments/couriers'
      ];

      let success = false;
      
      for (const endpoint of possibleEndpoints) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}?token=${token}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setCouriers(Array.isArray(data) ? data : data.couriers || []);
            success = true;
            console.log('✅ Couriers loaded from:', endpoint);
            break;
          }
        } catch (err) {
          continue; // Încerc următorul endpoint
        }
      }

      if (!success) {
        console.log('ℹ️ Nu s-au putut încărca curierii - folosesc curierii din răspunsurile shipment-urilor');
        // Extrag curierii din datele existente ale shipment-urilor
        extractCouriersFromShipments();
      }
    } catch (error) {
      console.log('ℹ️ Endpoint pentru curieri nu este disponibil - extrag din datele existente');
      extractCouriersFromShipments();
    }
  }, [token]);

  // Funcție pentru extragerea curierilor din datele existente
  const extractCouriersFromShipments = useCallback(() => {
    if (shipments.length > 0) {
      const uniqueCouriers = [];
      const courierMap = new Map();
      
      shipments.forEach(shipment => {
        if (shipment.courier && !courierMap.has(shipment.courier.id)) {
          courierMap.set(shipment.courier.id, shipment.courier);
        }
      });
      
      setCouriers(Array.from(courierMap.values()));
    }
  }, [shipments]);

  // Funcție pentru actualizarea statusului unui shipment
  const updateShipmentStatus = useCallback(async (shipmentId, newStatusId) => {
    if (!token) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/shipments/${shipmentId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          status_id: newStatusId
        })
      });

      if (response.ok) {
        // Actualizare locală în shipments
        setShipments(prevShipments => 
          prevShipments.map(shipment => 
            shipment.id === shipmentId 
              ? { ...shipment, status_id: newStatusId, date_updated: new Date().toISOString() }
              : shipment
          )
        );
        
        message.success('Statusul livrării a fost actualizat cu succes!');
        return true;
      } else {
        message.error('Eroare la actualizarea statusului livrării');
        return false;
      }
    } catch (error) {
      console.error('Eroare la actualizarea statusului livrării:', error);
      message.error('Eroare de conexiune');
      return false;
    }
  }, [token]);

  // Funcție pentru actualizarea AWB-ului
  const updateShipmentAWB = useCallback(async (shipmentId, awb) => {
    if (!token) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/shipments/${shipmentId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          awb: awb
        })
      });

      if (response.ok) {
        // Actualizare locală în shipments
        setShipments(prevShipments => 
          prevShipments.map(shipment => 
            shipment.id === shipmentId 
              ? { ...shipment, awb: awb, date_updated: new Date().toISOString() }
              : shipment
          )
        );
        
        message.success('AWB-ul a fost actualizat cu succes!');
        return true;
      } else {
        message.error('Eroare la actualizarea AWB-ului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la actualizarea AWB-ului:', error);
      message.error('Eroare de conexiune');
      return false;
    }
  }, [token]);

  // Funcție pentru crearea unui shipment nou
  const createShipment = useCallback(async (shipmentData) => {
    if (!token) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/shipments?token=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(shipmentData)
      });

      if (response.ok) {
        message.success('Livrarea a fost creată cu succes!');
        // Reîncărcăm datele
        fetchShipments();
        return true;
      } else {
        message.error('Eroare la crearea livrării');
        return false;
      }
    } catch (error) {
      console.error('Eroare la crearea livrării:', error);
      message.error('Eroare de conexiune');
      return false;
    }
  }, [token, fetchShipments]);

  // Auto-loading la mount și la schimbarea dependințelor
  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  // Loading statusurilor și curierilor la mount și după încărcarea shipment-urilor
  useEffect(() => {
    fetchShipmentStatuses();
    fetchCouriers();
  }, [fetchShipmentStatuses, fetchCouriers]);

  // Extrag statusurile și curierii din shipment-uri dacă nu s-au încărcat din endpoint-uri
  useEffect(() => {
    if (shipments.length > 0) {
      if (shipmentStatuses.length === 0) {
        extractStatusesFromShipments();
      }
      if (couriers.length === 0) {
        extractCouriersFromShipments();
      }
    }
  }, [shipments, shipmentStatuses.length, couriers.length, extractStatusesFromShipments, extractCouriersFromShipments]);

  return {
    shipments,
    loading,
    pagination,
    shipmentStatuses,
    couriers,
    overallSummary,
    filters,
    applyFilters,
    clearFilters,
    handleTableChange,
    fetchShipments,
    fetchShipmentStatuses,
    fetchCouriers,
    updateShipmentStatus,
    updateShipmentAWB,
    createShipment,
    setShipments
  };
};
